import { useCallback, useEffect } from 'react';
import _ from 'lodash';

/**
 * A custom hook that returns a debounced version of the provided function.
 *
 * @param callback The function to debounce
 * @param delay The delay in milliseconds
 * @param dependencies Array of dependencies for the callback
 * @returns A debounced version of the callback
 */
const useDebounce = <T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  dependencies: any[] = [],
): ((...args: Parameters<T>) => void) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCallback = useCallback(
    _.debounce((...args: Parameters<T>) => {
      callback(...args);
    }, delay),
    [delay, ...dependencies],
  );

  useEffect(
    () => () => {
      debouncedCallback.cancel();
    },
    [debouncedCallback],
  );

  return debouncedCallback;
};

export default useDebounce;
